import cx from 'classnames';
import React, { cloneElement, forwardRef, useRef, useMemo, useEffect } from 'react';
import { Icon } from '../../atoms';
import Button from '../../atoms/Button';
import Text from '../../atoms/Text';
import { ConditionalView } from '../../utils/ConditionalView';
import Popover, { PopoverHandle } from '../Popover';
import optionStyle from './Option/Option.module.scss';
import { SelectProvider } from './Select.Context';
import styles from './Select.module.scss';
import usePresenter from './Select.presenter';
import type { SelectProps, SelectViewProps } from './Select.types';

/**
 * Select component for displaying a dropdown with options.
 * Uses Popover for the dropdown functionality.
 *
 * @param props - Props for configuring the Select component.
 * @returns Element representing the select.
 */
const SelectComponent = forwardRef<PopoverHandle, SelectViewProps>((props, ref) => {
  const {
    variantProps,
    isOpen,
    selectedValue,
    resetSelectedValue,
    triggerProps,
    expandIcon,
    SelectedView,
    placeholder,
    leadingIcon,
    disabled,
    hasDefaultOption,
    menuProps,
    popoverOffset,
    onOptionsOpen,
    onOptionsClose,
    displayValue,
    multiple,
  } = usePresenter(props);

  const expandIconView = (
    <div className={cx(styles.expandIcon, {
      [styles.collapseIcon]: isOpen,
    })}>
      <ConditionalView condition={expandIcon}>
        {expandIcon}
      </ConditionalView>
      <ConditionalView condition={!expandIcon}>
        <Icon
          icon='ExpandMore'
          style='Filled'
          size='Small'
        />
      </ConditionalView>
    </div>
  );

  const selectedComponent = (
    <>
      <ConditionalView condition={Boolean(SelectedView && selectedValue)}>
        {SelectedView && <SelectedView selectedValue={selectedValue} />}
      </ConditionalView>
      <ConditionalView condition={!(SelectedView && selectedValue)}>
        <Text className={cx(styles.title)} weight={'Regular'} type='BodySmall'>
          {displayValue}
        </Text>
      </ConditionalView>
    </>
  );

  // Function to render the select trigger
  const selectTrigger = (
    <>
      <Button
        {...triggerProps}
        className={cx(styles.trigger)}
        leadingIcon={leadingIcon}
        trailingIcon={expandIconView}
        disabled={disabled}
        alignContent='SpaceBetween'
        {...variantProps}
      >
        {selectedComponent}
      </Button>

      {/* Hidden render of children to register options immediately */}
      <div style={{ display: 'none' }}>
        {props.children}
      </div>
    </>
  );

  // Function to render the options
  const options = (
    <ul className={cx(styles.options)} >
      <ConditionalView condition={hasDefaultOption}>
        <li className={cx(optionStyle.option)} onClick={resetSelectedValue}>
          <Text weight={'Regular'} type='BodySmall'>{placeholder}</Text>
        </li>
      </ConditionalView>
      {Array.isArray(props.children) ? (
        props.children.map((child, index) => cloneElement(child, { key: index, closeSelectOptions: onOptionsClose }))
      ) : (
        cloneElement(props.children, { closeSelectOptions: onOptionsClose })
      )}
    </ul>
  );

  return (
    <Popover
      ref={ref}
      {...triggerProps}
      className={cx(styles.select, props.className)}
      target={selectTrigger}
      content={options}
      show={isOpen}
      onOpen={onOptionsOpen}
      onHide={onOptionsClose}
      direction='Down'
      alignment='Start'
      action='Click'
      offset={popoverOffset}
      contentProps={menuProps}
      disabled={disabled}
      keepContentOnItemClick={multiple}
      {...variantProps}
    />
  );
});

/**
 * Select component that wraps SelectComponent with SelectProvider.
 * Provides state management and context to SelectComponent.
 *
 * @param props - Props for configuring the Select component.
 * @returns JSX element that renders the SelectComponent wrapped with SelectProvider.
 */
const Select = (props: SelectProps): JSX.Element => {
  const {
    value,
    multiple,
    disabled,
    onValueChange,
    hasDefaultOption,
    invalid,
    ...restProps
  } = props;

  const popoverOverRef = useRef<PopoverHandle>(null);

  const closeSelectOptions = () => {
    popoverOverRef.current?.close();
  };

  return (
    <SelectProvider
      value={value}
      multiple={multiple}
      disabled={disabled}
      invalid={invalid}
      onValueChange={onValueChange}
      hasDefaultOption={hasDefaultOption}
      closeSelectOptions={closeSelectOptions}
    >
      <SelectComponent
        ref={popoverOverRef}
        {...restProps}
        multiple={multiple || false}
        hasDefaultOption={hasDefaultOption}
      />
    </SelectProvider>
  );
};

export default Select;
